import { Link, useNavigate } from 'react-router-dom';
import { Check, Video, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { PLAN_INFO, billingApi, type PlanTier } from '@/features/billing/billing.api';
import { useAuthContext } from '@/features/auth/AuthProvider';
import { extractError } from '@/lib/axios';
import { config } from '@/config/env';

const ACCENTS: Record<PlanTier, string> = {
  FREE: 'from-slate-400 to-slate-500',
  PRO: 'from-brand-500 to-indigo-600',
  BUSINESS: 'from-fuchsia-500 to-purple-600',
  ENTERPRISE: 'from-amber-500 to-orange-600',
};

export default function PricingPage(): JSX.Element {
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();

  const choose = async (tier: PlanTier) => {
    if (!isAuthenticated) return navigate('/register');
    if (tier === 'FREE') return navigate('/dashboard');
    try {
      const url = await billingApi.checkout(tier as Exclude<PlanTier, 'FREE'>);
      window.location.href = url;
    } catch (err) {
      toast.error(extractError(err).message);
    }
  };

  return (
    <div className="relative min-h-full overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-brand-400/30 blur-3xl dark:bg-brand-600/20" />
        <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-fuchsia-400/20 blur-3xl dark:bg-fuchsia-600/10" />
      </div>

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 text-xl font-extrabold">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white shadow-lg">
            <Video size={18} />
          </span>
          {config.appName}
        </Link>
        <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 dark:text-slate-300">
          Log in
        </Link>
      </header>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-12 text-center">
        <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/70 px-4 py-1.5 text-sm font-medium text-brand-700 backdrop-blur dark:border-brand-800 dark:bg-slate-900/70 dark:text-brand-300">
          <Sparkles size={14} /> 14-day money-back guarantee
        </div>
        <h1 className="text-5xl font-black tracking-tight">
          Simple,{' '}
          <span className="bg-gradient-to-r from-brand-600 via-indigo-500 to-fuchsia-500 bg-clip-text text-transparent">
            transparent
          </span>{' '}
          pricing
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-slate-600 dark:text-slate-400">
          Choose the plan that fits your team. Upgrade, downgrade or cancel anytime.
        </p>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PLAN_INFO.map((p) => {
            const popular = p.tier === 'PRO';
            return (
              <div
                key={p.tier}
                className={`relative flex flex-col rounded-3xl border bg-white/80 p-7 text-left shadow-sm backdrop-blur transition hover:-translate-y-1.5 hover:shadow-2xl dark:bg-slate-900/70 ${
                  popular
                    ? 'border-transparent ring-2 ring-brand-500 dark:ring-brand-500'
                    : 'border-slate-200/70 dark:border-slate-800'
                }`}
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-3 py-1 text-xs font-bold text-white shadow-md">
                    MOST POPULAR
                  </span>
                )}
                <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${ACCENTS[p.tier]} text-white shadow-lg`}>
                  <Video size={20} />
                </div>
                <h3 className="text-xl font-bold">{p.name}</h3>
                <p className="mt-2">
                  <span className="bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-4xl font-black text-transparent dark:from-white dark:to-slate-300">
                    {p.price.split('/')[0]}
                  </span>
                  {p.price.includes('/') && <span className="text-sm text-slate-500">/{p.price.split('/')[1]}</span>}
                </p>
                <ul className="mt-5 flex-1 space-y-3 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/40">
                        <Check size={12} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => choose(p.tier)}
                  className={`mt-7 rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    popular
                      ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-lg hover:shadow-xl'
                      : 'border border-slate-300 text-slate-700 hover:border-brand-400 hover:text-brand-600 dark:border-slate-700 dark:text-slate-200'
                  }`}
                >
                  {p.tier === 'FREE' ? 'Get started' : `Choose ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-sm text-slate-500">
          All plans include HD video, screen sharing, chat and whiteboard. Need something custom?{' '}
          <Link to="/register" className="font-medium text-brand-600 hover:underline">Contact us</Link>.
        </p>
      </section>
    </div>
  );
}
